import { useState, useEffect, useRef } from "react";
import { useOrderingMessage } from "./components/useOrderingMessage";
import {
  ArrowLeftOutlined,
  BellOutlined,
  LoadingOutlined,
  LoginOutlined,
  PoweroffOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { logout, setAccess } from "../../store/authSlice";
import { denyAccess } from "../../api/access";
import { verifyPasswordForLogout } from "../../api/auth";
import {
  getAccountIsPersonal,
  getSharedAccountName,
  getTerminalToken,
} from "../../lib/auth";
import {
  consumeLocalTicketDropped,
  decodeEdgeTicketPayload,
} from "../../lib/edge/edgeTicket";
import { useIsEdgeRestricted } from "../../hooks/useEdgeMode";
import { getRestaurantName } from "../../lib/restaurant";
import { useViewport } from "../../hooks/useViewport";
import { useSharedAccountInfo } from "../../hooks/useSharedAccountInfo";
import {
  isPushSupported,
  usePushFeatureEnabled,
} from "../../hooks/usePushSubscription";
import PushSubscribersModal from "../../components/push/PushSubscribersModal";
import EdgeRestrictedNotice from "../../components/EdgeRestrictedNotice";
import { APP_VERSION } from "../../lib/version";
import LogoutConfirmModal from "./components/LogoutConfirmModal";
import KeypadThemeButton from "./components/KeypadThemeButton";
import KeypadOperationsButton from "./components/KeypadOperationsButton";
import { useReloadOnNewVersion } from "../../hooks/useReloadOnNewVersion";
import BrandLogo from "../../components/BrandLogo";

interface CodeKeypadProps {
  onCodeSubmit: (code: string) => Promise<void>;
  loading?: boolean;
}

const keypadRows = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
];

// ЕДНА мащабна единица за ЦЕЛИЯ екран: --u = 1px при 1440px viewport
// (0.06944vw), плавно и ЕДНОВРЕМЕННО за всеки елемент — от логото до хинта.
// Всички размери по-долу са calc(var(--u)*<px от дизайна при 1440>), затова
// нищо не може да изостане или да „прескочи" при зуум/друг екран. На телефон
// (под lg) --u е фиксирано 1px — там layout-ът е друг и не се мащабира.
const digitBtn =
  "h-[calc(var(--u)*68)] w-full rounded-[calc(var(--u)*14)] border border-pos-line3 bg-pos-card font-pos-mono text-[length:calc(var(--u)*26)] font-semibold text-pos-ink shadow-[0_2px_8px_rgba(60,40,20,0.05)] hover:border-pos-accent/40 hover:bg-pos-marked active:scale-[0.98] transition disabled:opacity-40 disabled:shadow-none";
const utilBtn =
  "h-[calc(var(--u)*68)] w-full rounded-[calc(var(--u)*14)] border border-pos-ctl bg-pos-panel text-[length:calc(var(--u)*15)] font-semibold text-pos-soft2 hover:border-pos-accent/40 hover:bg-pos-marked active:scale-[0.98] transition disabled:opacity-40";

export default function CodeKeypad({
  onCodeSubmit,
  loading = false,
}: CodeKeypadProps) {
  const { t, i18n } = useTranslation();
  const message = useOrderingMessage();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isDesktop } = useViewport();

  const [code, setCode] = useState("");
  // Предупреждението за лиценза на служителя, влязъл с кода си, чака над
  // клавиатурата, докато дойде неговото `/user/info` — тихо презареждане
  // точно тогава би го изгубило (BE вече е отбелязал показа).
  const licenseNoticePending = useAppSelector(
    (state) => state.auth.licenseNotice !== null,
  );
  // Смяната на смяна минава оттук: ако на сървъра има нова версия, документът
  // се презарежда тихо, преди някой да е започнал работа (docs/pwa.md).
  // Само с празна клавиатура — човек, който вече пише ПИН, не бива да види
  // екрана си нулиран.
  useReloadOnNewVersion(code.length === 0 && !licenseNoticePending);
  // Реф с последния код — за коректен Enter от клавиатурата (без stale closure).
  const codeRef = useRef("");
  codeRef.current = code;
  // Реф-огледало „има ли отворен наш модал" (известия/тема) за keydown
  // handler-а (без ре-абониране).
  const overlayOpenRef = useRef(false);
  const [themeModalOpen, setThemeModalOpen] = useState(false);
  const [operationsOpen, setOperationsOpen] = useState(false);

  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  // Известия на ниво общ акаунт: списък на абонираните на това устройство,
  // само премахване. Звънецът се крие при изключен push/неподдържан браузър.
  const [pushModalOpen, setPushModalOpen] = useState(false);
  overlayOpenRef.current = pushModalOpen || themeModalOpen || operationsOpen;
  const pushFeatureEnabled = usePushFeatureEnabled();
  const showPushBell = pushFeatureEnabled && isPushSupported();

  const access = useAppSelector((s) => s.auth.access);
  const username = useAppSelector((s) => s.auth.username);

  // Часовникът в левия панел — жив, точност до минута стига, но 1s е евтино.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const locale = i18n.language?.startsWith("bg") ? "bg-BG" : "en-GB";
  const dateStr = now.toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const timeStr = now.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });

  // Името на заведението — от /user/info на споделената сесия (не пипа Redux/auth,
  // гейтът остава на пин екрана); fallback е slug-ът от поддомейна.
  const { data: sharedInfo } = useSharedAccountInfo();
  const venueName = sharedInfo?.restaurant_display_name || getRestaurantName();

  const terminalName = getSharedAccountName() || username || "";

  // Ограничен локален режим (§14.3.1): билетът на терминала е изтекъл и през
  // касата минават само двата входа. ПИН-ът работи, но пътят до „потребител и
  // парола" е само оттук — иначе общ терминал без валиден код няма изход.
  const edgeRestricted = useIsEdgeRestricted();

  // „Върнахте се, защото интернетът се върна" — маркерът е записан от
  // `lib/edge/edgeMode.ts::leaveLocalMode`, когато размяната на локалния билет
  // не е минала и човекът е свален до този екран. Консумира се ВЕДНЪЖ (React
  // StrictMode монтира ефектите двойно в dev, затова стойността живее в state).
  const [droppedTicketNotice, setDroppedTicketNotice] = useState(false);
  useEffect(() => {
    if (consumeLocalTicketDropped()) setDroppedTicketNotice(true);
  }, []);

  // Чия парола проверява модалът за изход: на акаунта, с чийто токен би
  // тръгнала заявката — терминалният X. Името му трябва и БЕЗ интернет, за да
  // има с какво да се пита касата (`api/auth.ts::verifyPasswordForLogout`):
  // отговорът на облака го носи, а при вход СРЕЩУ КАСАТА го носи и самият
  // локален билет. Няма ли нито едното — проверката минава по стария път.
  const terminalUsername =
    sharedInfo?.username ??
    decodeEdgeTicketPayload(getTerminalToken())?.username ??
    null;

  const handleNumberClick = (num: string) => {
    if (loading) return;
    setCode((prev) => prev + num);
  };

  const handleBackspace = () => {
    if (loading) return;
    setCode((prev) => prev.slice(0, -1));
  };

  const handleClear = () => {
    if (loading) return;
    setCode("");
  };

  // Без ограничение за дължина/тип — код може да е цифри, букви, символи.
  const handleSubmit = async (value = code) => {
    if (loading) return;
    if (value.length === 0) {
      message.warning(t("codeKeypad.enterCode"));
      return;
    }
    await onCodeSubmit(value);
    setCode("");
  };

  // Хардуерна клавиатура: всеки печатим символ (букви/цифри/символи), Enter,
  // Backspace, Escape. Няма <input>, за да не изскача системната клавиатура.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (loading) return;

      // При отворен модал (известия/тема/фискални операции) клавишите не пипат
      // кода (Esc-ът на модала се поема от antd, но печатимите знаци биха се
      // трупали тук).
      if (overlayOpenRef.current) return;

      // Не прихващай клавиши, когато фокусът е в поле за въвеждане (напр. полето
      // за парола в модала за изход) — иначе паролата се трупа като „код" и на
      // Enter се праща login-with-code с грешен код („Невалиден код").
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target?.isContentEditable
      ) {
        return;
      }

      if (e.key === "Enter") {
        void handleSubmit(codeRef.current);
        return;
      }
      if (e.key === "Backspace") {
        setCode((prev) => prev.slice(0, -1));
        return;
      }
      if (e.key === "Escape") {
        setCode("");
        return;
      }
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        setCode((prev) => prev + e.key);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Изходът е 1:1 логиката от стария Navbar на пин екрана: тестов режим →
  // отказва достъпа; общ терминал → пълен изход с парола (освобождава лиценза).
  const handleLogout = async () => {
    if (access) {
      try {
        await denyAccess();
      } catch (error) {
        console.error("Deny access error:", error);
      }
      dispatch(setAccess(false));
      navigate("/", { replace: true });
      return;
    }
    if (getAccountIsPersonal() === false) {
      setLogoutModalOpen(true);
    } else {
      await dispatch(logout());
      navigate("/login", { replace: true });
    }
  };

  const handleLogoutConfirm = async (password: string) => {
    setLogoutLoading(true);
    try {
      // В локален режим проверката минава през касата: облачният
      // `/user/verify-password` го няма там и досега изходът офлайн беше
      // невъзможен — терминалът оставаше заключен на този екран.
      const result = await verifyPasswordForLogout(password, terminalUsername);

      if (result.valid) {
        await dispatch(logout());
        navigate("/login", { replace: true });
      } else {
        message.error(t("logoutConfirm.invalidPassword"));
      }
    } catch {
      message.error(t("logoutConfirm.verificationError"));
    } finally {
      setLogoutLoading(false);
      setLogoutModalOpen(false);
    }
  };

  // Точките растат с кода (кодът е с произволна дължина) — минимум 4 гнезда.
  const dotCount = Math.max(4, code.length);

  // Брандът в две големини: голям за левия десктоп панел (лого + „UnrealSoft"
  // и слоганът в моно под него — като на екрана за вход), компактен за хедъра
  // на телефон/таблет (там мястото се дели с терминала и „Изход").
  // Словомарката е еднаква на двата езика — не минава през i18n.
  const makeBrand = (isLargeBrand: boolean) => (
    <div
      className={`flex items-center ${
        isLargeBrand ? "gap-[calc(var(--u)*14)]" : "gap-[calc(var(--u)*12)]"
      }`}
    >
      <BrandLogo
        className={
          isLargeBrand
            ? "h-[calc(var(--u)*76)] w-[calc(var(--u)*76)]"
            : "h-[calc(var(--u)*40)] w-[calc(var(--u)*32)]"
        }
      />
      <div className="min-w-0">
        <div
          className={`font-bold text-pos-ink ${
            isLargeBrand
              ? "text-[length:calc(var(--u)*34)] leading-none tracking-[-1.2px]"
              : "text-[length:calc(var(--u)*15)] leading-tight"
          }`}
        >
          Unreal<span className="text-brand-ink">Soft</span>
        </div>
        {isLargeBrand ? (
          <div className="mt-[calc(var(--u)*6)] font-pos-mono text-[length:calc(var(--u)*11)] font-medium uppercase leading-none tracking-[2.4px] text-pos-muted">
            {t("login.eyebrow")}
          </div>
        ) : (
          <div className="text-[length:calc(var(--u)*12)] text-pos-muted">
            {t("modules.ordering")}
          </div>
        )}
      </div>
    </div>
  );

  // Дискретен звънец до „Изход" — известията на това устройство (само махане).
  const pushBellButton = showPushBell ? (
    <button
      type="button"
      onClick={() => setPushModalOpen(true)}
      title={t("push.deviceTitle")}
      className="inline-flex h-[calc(var(--u)*36)] w-[calc(var(--u)*36)] shrink-0 items-center justify-center rounded-[calc(var(--u)*10)] border border-shade-3 bg-transparent text-[length:calc(var(--u)*14)] text-pos-body hover:bg-surface/60 transition"
    >
      <BellOutlined />
    </button>
  ) : null;

  // Темата на терминала (общия акаунт) — горе вдясно, виж KeypadThemeButton.
  const themeButton = <KeypadThemeButton onOpenChange={setThemeModalOpen} />;

  // Фискалните операции на Маркиране — бутон до темата, без право
  // (виж KeypadOperationsButton).
  const operationsButton = <KeypadOperationsButton onOpenChange={setOperationsOpen} />;

  const exitButton = (
    <button
      type="button"
      onClick={handleLogout}
      className="inline-flex h-[calc(var(--u)*36)] shrink-0 items-center gap-[calc(var(--u)*6)] rounded-[calc(var(--u)*10)] border border-shade-3 bg-transparent px-[calc(var(--u)*12)] text-[length:calc(var(--u)*13)] font-semibold text-pos-body hover:bg-surface/60 transition"
    >
      <PoweroffOutlined className="text-[length:calc(var(--u)*12)]" />
      {t("common.logout")}
    </button>
  );

  const versionLine = (
    <p className="text-[length:calc(var(--u)*11.5)] text-pos-muted">
      {t("common.restaurantSystem")} ·{" "}
      <span className="font-pos-mono">v{APP_VERSION}</span>
    </p>
  );

  // Брандът е еднакъв на двата езика — не минава през i18n.
  const poweredBy = (
    <p className="text-[length:calc(var(--u)*12)] text-pos-faint">
      Powered by <span className="font-semibold text-pos-dim">UnrealSoft</span>
    </p>
  );

  return (
    <>
      <div className="flex min-h-dvh bg-pos-canvas text-pos-ink [--u:1px] lg:[--u:clamp(0.71px,0.06944vw,1.3px)]">
        {/* Ляв инфо панел — само десктоп. Геометрията и фонът са 1:1 с hero
            панела на „Модули" (Modules.tsx), за да не „скача" панелът при
            навигация между двата екрана — същата ширина, фон, glows и grid. */}
        {isDesktop && (
          <aside className="relative flex w-[clamp(320px,28vw,640px)] shrink-0 flex-col overflow-hidden border-r border-shade-2 bg-sidebar px-[calc(var(--mu)*36)] pb-[calc(var(--mu)*32)] pt-[calc(var(--mu)*40)] [--mu:clamp(1px,0.06944vw,2px)]">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_55%_50%_at_15%_12%,var(--color-brand-tint)_0%,transparent_60%),radial-gradient(ellipse_70%_55%_at_85%_92%,var(--color-brand-tint2)_0%,transparent_65%)]"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-[linear-gradient(var(--color-shade-2)_1px,transparent_1px),linear-gradient(90deg,var(--color-shade-2)_1px,transparent_1px)] bg-[size:48px_48px] opacity-60 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_50%,#000_30%,transparent_80%)]"
            />

            <div className="relative z-[1]">{makeBrand(true)}</div>

            <div className="relative z-[1] flex flex-1 flex-col justify-center">
              <div className="font-pos-mono text-[length:calc(var(--u)*11)] font-semibold uppercase tracking-[.14em] text-pos-sec-head">
                {dateStr}
              </div>
              <div className="mt-[calc(var(--u)*8)] font-pos-mono text-[length:calc(var(--u)*56)] font-bold leading-none text-pos-ink">
                {timeStr}
              </div>

              <div className="mt-[calc(var(--u)*40)] font-pos-mono text-[length:calc(var(--u)*11)] font-semibold uppercase tracking-[.14em] text-pos-sec-head">
                {t("codeKeypad.venue")}
              </div>
              <div className="mt-[calc(var(--u)*4)] text-[length:calc(var(--u)*26)] font-extrabold leading-tight tracking-tight text-pos-ink">
                {venueName}
              </div>
            </div>

            <div className="relative z-[1]">
              {/* Полупрозрачна, за да се слива с топлия фон на панела (не плътно бяло) */}
              <div className="flex items-center justify-between gap-[calc(var(--u)*12)] rounded-[calc(var(--u)*14)] border border-shade-3 bg-surface/35 px-[calc(var(--u)*14)] py-[calc(var(--u)*12)]">
                <div className="flex min-w-0 items-center gap-[calc(var(--u)*8)]">
                  <span className="h-[calc(var(--u)*8)] w-[calc(var(--u)*8)] shrink-0 rounded-full bg-pos-served" />
                  <span className="truncate text-[length:calc(var(--u)*13.5)] font-semibold text-pos-ink">
                    {terminalName}
                  </span>
                  <span className="shrink-0 text-[length:calc(var(--u)*13)] text-pos-muted">
                    · {t("codeKeypad.online")}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-[calc(var(--u)*8)]">
                  {pushBellButton}
                  {exitButton}
                </div>
              </div>
              <div className="mt-[calc(var(--u)*12)]">{versionLine}</div>
            </div>
          </aside>
        )}

        <main className="relative flex flex-1 flex-col">
          {/* Компактен хедър — телефон/таблет */}
          {!isDesktop && (
            <header className="flex items-center justify-between gap-3 px-4 pt-4">
              {makeBrand(false)}
              {/* Пренася се на втори ред само на тесен телефон (с „Фискални
                  операции" и звънеца бутоните не се събират до бранда). */}
              <div className="flex flex-wrap items-center justify-end gap-2.5">
                <span className="hidden items-center gap-1.5 text-[12.5px] font-semibold text-pos-soft sm:flex">
                  <span className="h-2 w-2 rounded-full bg-pos-served" />
                  {terminalName}
                </span>
                {operationsButton}
                {themeButton}
                {pushBellButton}
                {exitButton}
              </div>
            </header>
          )}

          {/* Фискални операции и тема на терминала — горе вдясно (огледално на „Powered by" долу) */}
          {isDesktop && (
            <div className="absolute right-[calc(var(--u)*24)] top-[calc(var(--u)*20)] z-[1] flex items-center gap-[calc(var(--u)*8)]">
              {operationsButton}
              {themeButton}
            </div>
          )}

          <div className="grid flex-1 place-items-center px-[calc(var(--u)*16)] py-[calc(var(--u)*24)]">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22 }}
              className="w-full max-w-[calc(var(--u)*460)]"
            >
              {/* Ограничен локален режим (§14.3.1, docs/edge.md): билетът на
                  терминала е изтекъл — кодът минтва нов от касата. */}
              <EdgeRestrictedNotice
                variant="pos"
                className="mb-[calc(var(--u)*14)]"
              />
              {/* Връзката се е върнала и локалният билет е бил хвърлен — без
                  този ред екранът изглежда като изгонване без причина. */}
              {droppedTicketNotice && (
                <div
                  role="status"
                  aria-live="polite"
                  className="mb-[calc(var(--u)*14)] rounded-[calc(var(--u)*12)] border border-pos-offline-brd bg-pos-offline-bg px-[calc(var(--u)*14)] py-[calc(var(--u)*10)] text-[length:calc(var(--u)*13)] font-semibold leading-[1.3] text-pos-offline-ink"
                >
                  {t("edge.returnedToPin")}
                </div>
              )}
              <div className="rounded-[calc(var(--u)*22)] border border-pos-line3 bg-pos-card p-[calc(var(--u)*32)] shadow-[0_24px_60px_rgba(60,40,20,0.10)]">
                <h2 className="text-center text-[length:calc(var(--u)*26)] font-extrabold tracking-tight text-pos-ink">
                  {t("codeKeypad.title")}
                </h2>
                <p className="mt-[calc(var(--u)*6)] text-center font-pos-mono text-[length:calc(var(--u)*11)] font-semibold uppercase tracking-[.14em] text-pos-sec-head">
                  {t("codeKeypad.kicker")}
                </p>

                {/* Кодът се показва само като точки (маскиран) */}
                <div className="mt-[calc(var(--u)*24)] flex flex-wrap items-center justify-center gap-[calc(var(--u)*12)]">
                  {Array.from({ length: dotCount }).map((_, i) => (
                    <span
                      key={i}
                      className={`h-[calc(var(--u)*14)] w-[calc(var(--u)*14)] rounded-full transition-colors ${
                        i < code.length
                          ? "bg-pos-accent"
                          : "border border-pos-ctl bg-pos-panel"
                      }`}
                    />
                  ))}
                </div>

                <div className="mt-[calc(var(--u)*24)] grid grid-cols-3 gap-[calc(var(--u)*12)]">
                  {keypadRows.map((row) =>
                    row.map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => handleNumberClick(num)}
                        disabled={loading}
                        className={digitBtn}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {num}
                      </button>
                    )),
                  )}

                  <button
                    type="button"
                    onClick={handleClear}
                    disabled={loading || code.length === 0}
                    className={utilBtn}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {t("codeKeypad.clear")}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleNumberClick("0")}
                    disabled={loading}
                    className={digitBtn}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    0
                  </button>

                  <button
                    type="button"
                    onClick={handleBackspace}
                    disabled={loading || code.length === 0}
                    className={utilBtn}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <ArrowLeftOutlined className="text-[length:calc(var(--u)*20)]" />
                  </button>
                </div>

                {/* Бутон за вход (няма авто-submit, кодът е с произволна дължина) */}
                <button
                  type="button"
                  onClick={() => handleSubmit()}
                  disabled={loading || code.length === 0}
                  className="mt-[calc(var(--u)*16)] flex h-[calc(var(--u)*54)] w-full items-center justify-center gap-[calc(var(--u)*8)] rounded-[calc(var(--u)*14)] bg-pos-accent text-[length:calc(var(--u)*16)] font-bold text-white hover:bg-pos-accent-ink active:scale-[0.99] transition disabled:bg-pos-none-bg disabled:text-pos-dim"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {loading ? (
                    <LoadingOutlined spin />
                  ) : (
                    <LoginOutlined className="text-[length:calc(var(--u)*15)]" />
                  )}
                  {t("codeKeypad.submit")}
                </button>

                {/* Ограничен локален режим: касата приема САМО двата входа, а
                    до екрана за потребител и парола иначе няма как да се стигне
                    от този терминал — изходът с парола също минава през нея. */}
                {edgeRestricted && (
                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                    className={`${utilBtn} mt-[calc(var(--u)*12)]`}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {t("edge.loginWithPassword")}
                  </button>
                )}

                <p className="mt-[calc(var(--u)*16)] flex items-center justify-center gap-[calc(var(--u)*8)] text-[length:calc(var(--u)*12)] text-pos-muted">
                  <span className="inline-grid h-[calc(var(--u)*18)] w-[calc(var(--u)*22)] place-items-center rounded-[calc(var(--u)*5)] border border-pos-ctl text-[length:calc(var(--u)*11)] leading-none">
                    ⌨
                  </span>
                  {t("codeKeypad.hint")}
                </p>
              </div>
            </motion.div>
          </div>

          {!isDesktop && (
            <div className="flex flex-col items-center gap-1 pb-[calc(1rem+env(safe-area-inset-bottom,0px)*0.5)]">
              {versionLine}
              {poweredBy}
            </div>
          )}

          {/* Powered by UnrealSoft — долу вдясно (както в дизайна) */}
          {isDesktop && (
            <div className="absolute bottom-[calc(var(--u)*20)] right-[calc(var(--u)*24)]">
              {poweredBy}
            </div>
          )}
        </main>
      </div>

      <LogoutConfirmModal
        open={logoutModalOpen}
        onCancel={() => setLogoutModalOpen(false)}
        onConfirm={handleLogoutConfirm}
        loading={logoutLoading}
      />

      <PushSubscribersModal
        open={pushModalOpen}
        onClose={() => setPushModalOpen(false)}
      />
    </>
  );
}
